import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Currency } from '@/components/ui/currency'
import { ArrowLeft, Calendar, User, Eye, Clock, Tag, FileText } from 'lucide-react'
import { useParams, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { projectReportApi } from '@tatatemplate/shared-lib'
import { ShareButton } from '@/components/ShareButton'
import type { Report } from '@/types'

export default function ReportDetailPage() {
  const { slug } = useParams()
  const navigate = useNavigate()

  // Fetch single report by slug
  const { data: reportData, isLoading, error } = useQuery({
    queryKey: ['report', slug],
    queryFn: () => projectReportApi.getReportBySlug(slug!),
    enabled: !!slug
  })

  const report = reportData?.data

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
              <div className="h-64 bg-gray-200 rounded-xl mb-8"></div>
              <div className="space-y-4">
                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Button
              onClick={() => navigate('/reports')}
              className="mb-6 bg-white/20 hover:bg-white/30 text-blue-600 border border-blue-200"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Kembali ke Laporan
            </Button>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-12 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Laporan tidak ditemukan</h3>
                <p className="text-gray-500 mb-6">Laporan yang Anda cari mungkin telah dihapus atau tidak tersedia.</p>
                <Button onClick={() => navigate('/reports')}>
                  Kembali ke Halaman Laporan
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header with Back Button */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 backdrop-blur-sm bg-white/90">
        <div className="container mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/reports')}
            variant="ghost"
            className="hover:bg-gray-100 text-gray-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Kembali ke Laporan
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Report Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200">
                {report.type}
              </Badge>
              {report.categories && report.categories.map((category: { id: string; name: string }) => (
                <Badge key={category.id} variant="outline" className="border-gray-300 text-gray-600">
                  <Tag className="h-3 w-3 mr-1" />
                  {category.name}
                </Badge>
              ))}
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {report.title}
            </h1>

            {report.summary && (
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                {report.summary}
              </p>
            )}

            {/* Report Meta */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-200">
              {report.reportDate && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-500" />
                  <span>{new Date(report.reportDate).toLocaleDateString('id-ID', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
              )}
              {report.author && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-green-500" />
                  <span>{report.author.name}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-500" />
                <span>{report.readTime || '10 menit baca'}</span>
              </div>

            </div>

            {/* Share Section */}
            <div className="flex items-center justify-between mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Bagikan Laporan Ini</h3>
                <p className="text-sm text-gray-600">Sebarkan transparansi dan akuntabilitas desa</p>
              </div>
              <ShareButton 
                title={report.title}
                description={report.summary}
                variant="default"
              />
            </div>
          </div>

          {/* Featured Image */}
          {report.featuredImage && (
            <div className="mb-8">
              <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden shadow-lg">
                <img 
                  src={report.featuredImage} 
                  alt={report.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          {/* Collage Images */}
          {report.collageImage && report.collageImage.length > 0 && (
            <Card className="border-0 shadow-lg mb-8">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-green-500" />
                    Galeri Foto
                  </span>
                  <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200">
                    {report.collageImage.length} Foto
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {report.collageImage.map((image, index) => (
                    <div key={index} className="relative group">
                      <div 
                        className="aspect-video rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                        onClick={() => window.open(image, '_blank')}
                      >
                        <img
                          src={image}
                          alt={`${report.title} - Foto ${index + 1}`}
                          className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        />
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                          Foto {index + 1}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          className="h-auto py-1 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <a href={image} download>
                            Download
                          </a>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Report Content */}
          <Card className="border-0 shadow-lg mb-8">
            <CardContent className="p-8">
              <div 
                className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: report.content || '' }}
              />
            </CardContent>
          </Card>

          {/* Attachments */}
          {report.attachments && report.attachments.length > 0 && (
            <Card className="border-0 shadow-lg mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  Lampiran
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {report.attachments.map((attachment, index) => (
                    <div key={attachment.id || index} className="relative group">
                      <div className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                        <img
                          src={attachment.url}
                          alt={attachment.filename}
                          className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        />
                      </div>
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {attachment.filename}
                        </p>
                        {attachment.size && (
                          <p className="text-xs text-gray-500">
                            {(attachment.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Project Information */}
          {report.project && (
            <Card className="border-0 shadow-lg mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  Informasi Proyek
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                    {report.project.featuredImage && (
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                        <img 
                          src={report.project.featuredImage} 
                          alt={report.project.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 mb-2">{report.project.title}</h4>
                      <p className="text-sm text-gray-600 mb-3">{report.project.description}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Status: </span>
                          <Badge variant="secondary" className="text-xs">
                            {report.project.status}
                          </Badge>
                        </div>
                        
                        {report.project.budget && (
                          <div>
                            <span className="text-gray-500">Budget: </span>
                            <Currency amount={report.project.budget} className="font-medium" />
                          </div>
                        )}
                        
                        {report.project.location && (
                          <div>
                            <span className="text-gray-500">Lokasi: </span>
                            <span className="text-gray-900">{report.project.location}</span>
                          </div>
                        )}
                        
                        {report.project.startDate && (
                          <div>
                            <span className="text-gray-500">Mulai: </span>
                            <span className="text-gray-900">
                              {new Date(report.project.startDate).toLocaleDateString('id-ID')}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Coordinator Information */}
                      {report.project.coordinatorName && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center gap-3">
                            {report.project.coordinatorAvatar && (
                              <img 
                                src={report.project.coordinatorAvatar} 
                                alt={report.project.coordinatorName}
                                className="w-8 h-8 rounded-full"
                              />
                            )}
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {report.project.coordinatorName}
                              </p>
                              {report.project.coordinatorPosition && (
                                <p className="text-xs text-gray-500">
                                  {report.project.coordinatorPosition}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Project Tags and Categories */}
                      {report.project.tags && report.project.tags.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs text-gray-500 mb-2">Tags Proyek:</p>
                          <div className="flex flex-wrap gap-1">
                            {report.project.tags.map((tag) => (
                              <Badge key={tag.id} variant="outline" className="text-xs border-blue-200 text-blue-700">
                                {tag.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {report.project.categories && report.project.categories.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 mb-2">Kategori Proyek:</p>
                          <div className="flex flex-wrap gap-1">
                            {report.project.categories.map((category) => (
                              <Badge key={category.id} variant="outline" className="text-xs border-green-200 text-green-700">
                                {category.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => navigate(`/projects/${report.project?.id}`)}
                      className="flex-shrink-0"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Lihat Proyek
                    </Button>
                  </div>

                  {report.progress !== null && report.progress !== undefined && report.type !== 'FINAL' && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-blue-900">Progress Proyek</span>
                        <span className="text-sm text-blue-600">{report.progress}%</span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${report.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {report.type === 'FINAL' && (
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium text-green-900">Laporan Final - Proyek Selesai</span>
                      </div>
                    </div>
                  )}

                  {report.budgetUsed && (
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-green-900">Budget Terpakai</span>
                        <Currency amount={report.budgetUsed} className="text-green-600 font-semibold" />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tags and Categories */}
          {((report.tags && report.tags.length > 0) || (report.categories && report.categories.length > 0)) && (
            <Card className="border-0 shadow-lg mb-8">
              <CardHeader>
                <CardTitle className="text-lg">Tags & Kategori</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {report.categories && report.categories.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Kategori:</p>
                    <div className="flex flex-wrap gap-2">
                      {report.categories.map((category) => (
                        <Badge key={category.id} variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">
                          <Tag className="h-3 w-3 mr-1" />
                          {category.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {report.tags && report.tags.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Tags:</p>
                    <div className="flex flex-wrap gap-2">
                      {report.tags.map((tag) => (
                        <Badge key={tag.id} variant="outline" className="border-gray-300 text-gray-600">
                          {tag.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Bottom Share Section */}
          <Card className="border-0 shadow-lg mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Laporan Transparan?</h3>
                  <p className="text-sm text-gray-600">Bagikan untuk meningkatkan akuntabilitas</p>
                </div>
                <ShareButton 
                  title={report.title}
                  description={report.summary}
                  variant="outline"
                />
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-center">
            <Button
              onClick={() => navigate('/reports')}
              variant="outline"
              className="border-gray-300 hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Kembali ke Semua Laporan
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}